import React, { PropTypes } from 'react';
import { Dialog, List, ListItem, FontIcon, IconButton } from 'material-ui';
import { connect } from 'react-redux';
import FlatButton from '../../../../tc-ui/flat-button-with-loader';
import EditRateSet from './edit-rate-set';
import AddRateSet from '../add-rate-set';
import R from 'ramda';

const EditRates = (props) => {
  const {
    saving,
    onRequestClose,
    contractId,
    productId,
    saveProductInfoChanges,
    data,
    productType,
    dialogOpen,
    onTextFieldChangeWithKeyPath,
    productName,
    ratePackageName,
  } = props;
  if (R.isEmpty(data)) return <div/>;
  const currentRateSet = data.rateSets[data.rateSetsIndex];
  const exists = R.allPass([
    val => R.complement(R.isNil)(val),
    val => R.complement(R.equals(0))(val),
    val => R.complement(R.equals('0'))(val),
    val => R.complement(R.equals(''))(val),
  ]);
  const getRateWithMaxAdultsPlusChild = R.compose(
      R.last,
      R.sort((a, b) => {
        const rateA = R.propOr(0, 'maxAdultsPlusChild', a);
        const rateB = R.propOr(0, 'maxAdultsPlusChild', b);
        if (rateA > rateB) return 1;
        if (rateB > rateA) return -1;
        return 0;
      }),
      R.filter(rate => exists(R.prop('maxAdultsPlusChild', rate))),
      R.propOr([], 'rates')
    );
  const maxAdultsPlusChild = R.propOr(0, 'maxAdultsPlusChild', getRateWithMaxAdultsPlusChild(currentRateSet) || {});
  const getUsedRateSetNamesAndCodes = indexToExclude => data.rateSets
  .filter((_, i) => i !== indexToExclude)
  .map(R.pick(['rateSetName', 'rateSetCode']));

  const normalizeMinMaxDays = obj => {
    return (obj.daysOfWeek || []).reduce((newObj, day) => {
      newObj[day] = {
        minStay: obj.minStay || 1,
        maxStay: obj.maxStay || 999,
      };
      return newObj;
    }, {});
  };
  const getGroupedUsedMinMaxDays = (indexToExclude) => {
    return R.call(R.compose(
      R.reduce((daysMinMaxMap, obj) => {
        if (obj.Monday) daysMinMaxMap.Monday.push(obj.Monday);
        if (obj.Tuesday) daysMinMaxMap.Tuesday.push(obj.Tuesday);
        if (obj.Wednesday) daysMinMaxMap.Wednesday.push(obj.Wednesday);
        if (obj.Thursday) daysMinMaxMap.Thursday.push(obj.Thursday);
        if (obj.Friday) daysMinMaxMap.Friday.push(obj.Friday);
        if (obj.Saturday) daysMinMaxMap.Saturday.push(obj.Saturday);
        if (obj.Sunday) daysMinMaxMap.Sunday.push(obj.Sunday);
        return daysMinMaxMap;
      }, { Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [], Sunday: [] }),
      R.map(R.compose(
        normalizeMinMaxDays,
        R.pick(['minStay', 'maxStay', 'daysOfWeek'])
      )),
      R.addIndex(R.filter)((_, i) => i !== indexToExclude)
    ), data.rateSets);
  };

  const overlappingDays = (rateSet, rateSetIndex) => {
    const groupedUsedMinMaxDays = getGroupedUsedMinMaxDays(rateSetIndex);
    const currentGroupedMinMax = normalizeMinMaxDays(rateSet);
    const isMinMaxOverlap = R.curry((objA, objB) => {
      const isBetweenRateSetMinMax = num => R.lte(num, objA.maxStay || 999) && R.gte(num, objA.minStay || 1);
      return isBetweenRateSetMinMax(objB.minStay || 1) || isBetweenRateSetMinMax(objB.maxStay || 999);
    });
    if (rateSet.daysOfWeek && rateSet.daysOfWeek.some(day => {
      const currentMinMaxObj = currentGroupedMinMax[day];
      const usedMinMaxArray = groupedUsedMinMaxDays[day];
      if (productType === 'Non-Accommodation') return usedMinMaxArray && usedMinMaxArray.length > 0;
      return usedMinMaxArray.some(objA => isMinMaxOverlap(objA, currentMinMaxObj));
    })) {
      return productType === 'Accommodation' ? `Rate sets cannot have overlapping min/max night stays and be applied to the same days of the week.`
        : `Rate sets cannot have overlapping days of the week.`;
    }
  };
  const invalidRateSetNameAndCode = (rateSet, rateSetsIndex) => {
    const usedRateSetNamesAndCodes = getUsedRateSetNamesAndCodes(rateSetsIndex);

    if (usedRateSetNamesAndCodes.some(nameAndCode => {
      if (nameAndCode.rateSetName === rateSet.rateSetName) {
        if (!nameAndCode.rateSetCode && !rateSet.rateSetCode) return true;
        return nameAndCode.rateSetCode === rateSet.rateSetCode;
      }
    })) {
      return `You have already entered a ${rateSet.rateSetName} rate set using ${rateSet.rateSetCode ? `the ${rateSet.rateSetCode}` : ' an empty'} rate set code for this season. Please enter a new rate set name or rate set code so your agents know which rate is applicable.`;
    }
  };

  const ratesTableNotComplete = rateSet => {
    if (rateSet.rates && rateSet.rates.every(rate => rate.notApplicable)) return 'You need to include at least one rate for this rate set to be valid.';
  };
  const isRateSetValid = (rateSet, rateSetsIndex) => {
    if (invalidRateSetNameAndCode(rateSet, rateSetsIndex) || ratesTableNotComplete(rateSet) || overlappingDays(rateSet, rateSetsIndex)) return false;
    if (productType === 'Accommodation' && !getRateWithMaxAdultsPlusChild(rateSet)) return false;
    if (!rateSet.rateSetName || !rateSet.daysOfWeek.length) return false;
    return true;
  };
  const invalidRSIndexes = data.rateSets.reduce((result, rs, i) => {
    if (!isRateSetValid(rs, i)) result.push(i);
    return result;
  }, []);
  return (
    <Dialog
      autoScrollBodyContent
      bodyStyle={{ padding: '0', paddingBottom: '12px' }}
      contentStyle={{ maxWidth: '1200px', width: '1200px' }}
      open={dialogOpen}
      actionsContainerStyle={{ boxShadow: '0 1px 6px rgba(0, 0, 0, 0.12), 0 1px 4px rgba(0, 0, 0, 0.24)' }}
      title={<div
        style={{ backgroundColor: '#00BCD4', color: 'white', fontSize: '18px', padding: '16px 2%' }}
      >
          Edit Rates for {data.seasonName} - {productName} {ratePackageName ? `(${ratePackageName})` : ''}
        </div>}
      actions={currentRateSet.new ? null : [
        <FlatButton
          label="Cancel"
          labelStyle={{ fontWeight: '600', marginRight: '15px' }}
          primary
          onTouchTap={onRequestClose}
        />,
        <FlatButton
          onTouchTap={() => saveProductInfoChanges({ contractId, productId, data, updateType: 'SAVE_RATES_CHANGES_ADD_EDIT' })}
          secondary
          loader={saving}
          disabled={!R.isEmpty(invalidRSIndexes)}
          label="save changes"
          labelStyle={{ fontWeight: '600' }}
        />,
      ]}
    >
      <div style={{ display: 'flex' }}>
        <List style={{ width: '265px', minWidth: '265px', marginRight: '16px', borderRight: '1px solid #e0e0e0' }}>
          {
            data.rateSets.map((rateSet, rateSetsIndex) => {
              return (
                <ListItem
                  key={rateSetsIndex}
                  rightIconButton={<IconButton
                    iconClassName="tc-icon-close"
                    iconStyle={{ color: 'rgba(0, 0, 0, 0.54)' }}
                    tooltip="delete rate set"
                    onTouchTap={() => {
                      onTextFieldChangeWithKeyPath({
                        keyPath: ['data'],
                        value: {
                          ...data,
                          rateSetsIndex: Math.max(0, data.rateSetsIndex - 1),
                          rateSets: data.rateSets.filter((_, i) => i !== rateSetsIndex),
                        },
                      });
                      if (rateSet.new) {
                        props.clearAddRateSetWizard();
                      }
                    }}
                  />}
                  style={R.merge({ color: 'rgba(0, 0, 0, 0.64)' }, data.rateSetsIndex === rateSetsIndex ? { backgroundColor: '#efefef' } : {})}
                  primaryText={<div style={{ display: 'flex', alignItems: 'center' }}>
                    {rateSet.rateSetName || 'New'}{rateSet.rateSetCode ? `(${rateSet.rateSetCode})` : ''}
                    {
                      R.contains(rateSetsIndex, invalidRSIndexes) && <FontIcon className="tc-icon-report" style={{ color: '#f44336', marginLeft: '4px' }}/>
                    }
                  </div>}
                  onTouchTap={() => onTextFieldChangeWithKeyPath({ keyPath: ['data', 'rateSetsIndex'], value: rateSetsIndex })}
                />
              );
            })
          }
          <ListItem
            leftIcon={<FontIcon className="tc-icon-add_circle" color="#00bcd4" />}
            primaryText="Add Rate Set"
            onTouchTap={() => {
              onTextFieldChangeWithKeyPath({
                keyPath: ['data'],
                value: {
                  ...data,
                  rateSets: R.append({ new: true }, data.rateSets),
                  rateSetsIndex: data.rateSets.length,
                },
              });
            }}
          />
        </List>
        {
          currentRateSet.new ? (
            <AddRateSet
              onRequestClose={onRequestClose}
              productName={productName}
              getOverlappingDaysError={rateSet => overlappingDays(rateSet, data.rateSetsIndex)}
              usedRateSetNamesAndCodes={getUsedRateSetNamesAndCodes(data.rateSetsIndex)}
              productType={productType}
              onFinish={rateSet => {
                props.clearAddRateSetWizard();
                onTextFieldChangeWithKeyPath({
                  keyPath: ['data', 'rateSets'],
                  value: data.rateSets.map(rs => !rs.new ? rs : rateSet),
                });
              }}
            />
          ) : (
            <EditRateSet
              {...props}
              {...currentRateSet}
              maxAdultsPlusChild={maxAdultsPlusChild}
              rateSetNameAndCodeError={invalidRateSetNameAndCode(currentRateSet, data.rateSetsIndex)}
              overlappingDaysError={overlappingDays(currentRateSet, data.rateSetsIndex)}
              ratesTableError={ratesTableNotComplete(currentRateSet)}
            />
          )
        }
      </div>
    </Dialog>
  );
};

EditRates.propTypes = {
  dialogOpen: PropTypes.bool,
  saving: PropTypes.bool,
  onRequestClose: PropTypes.func,
  onTextFieldChange: PropTypes.func,
  updateDaysOfWeek: PropTypes.func,
  updateTableCell: PropTypes.func,
  contractId: PropTypes.string,
  productId: PropTypes.string,
  productName: PropTypes.string,
  saveProductInfoChanges: PropTypes.func,
  data: PropTypes.object,
  productType: PropTypes.string,
  onTextFieldChangeWithKeyPath: PropTypes.func,
  clearAddRateSetWizard: PropTypes.func,
};

const mapStateToProps = ({ contracting }) => ({
  dialogOpen: contracting.addEditContractDialog.get('open'),
  paxMax: contracting.addRateSetWizard.get('paxMax'),
  twinBedsAvail: contracting.addRateSetWizard.get('twinBedsAvail'),
});

export default connect(mapStateToProps, dispatch => ({ clearAddRateSetWizard: () => dispatch({ type: 'CLEAR_ADD_RATE_SET_WIZARD' }) }))(EditRates);



// WEBPACK FOOTER //
// ./src/app/components/contracting/contract-activities-products/add-edit-dialog-content/edit-rates/edit-rates-container.jsx